import React, { useState } from 'react';
import VoxSmythIcons from './VoxSmythIcons';
import { extractErrorMessage } from './utils/extractErrorMessage';

const ForgotPasswordPage = ({ onNavigateHome }) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Design tokens matching VoxSmyth theme
  const RESET_TOKENS = {
    colors: {
      slate: '#2C3A4C',
      literary_gold: '#C8860D',
      antique_gold: '#D7B377',
      warm_parchment: '#F9F6F0',
      rich_parchment: '#E8DCC0',
      error_red: '#E74C3C',
      success_green: '#27AE60'
    },
    typography: {
      main_title: 'DM Serif Display, Georgia, serif',
      label: 'DM Sans, sans-serif',
      input: 'Inter, sans-serif',
      button: 'DM Sans, sans-serif'
    },
    layout: {
      form_width: '480px',
      input_height: '48px'
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://voxsmyth.com';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      console.log('📧 Sending password reset request for:', email);
      
      const response = await fetch(`${BACKEND_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Password reset email sent successfully');
        setIsSubmitted(true);
      } else {
        setError(extractErrorMessage(data, 'Failed to send reset email'));
      }
    } catch (error) {
      console.error('❌ Password reset request failed:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div 
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${RESET_TOKENS.colors.slate} 0%, #1A202C 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
        }}
      >
        {/* Floating particles background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                background: RESET_TOKENS.colors.antique_gold,
                opacity: Math.random() * 0.3 + 0.1,
                animation: `float ${Math.random() * 10 + 10}s linear infinite`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center max-w-md mx-auto">
          <div style={{ marginBottom: '32px' }}>
            <VoxSmythIcons.Mail size={64} color={RESET_TOKENS.colors.antique_gold} />
          </div>
          
          <h1
            style={{
              fontFamily: RESET_TOKENS.typography.main_title,
              fontSize: window.innerWidth < 768 ? '28px' : '36px',
              color: 'white',
              marginBottom: '16px'
            }}
          >
            Check Your Email
          </h1>
          
          <p
            style={{
              fontFamily: RESET_TOKENS.typography.input,
              fontSize: '18px',
              color: RESET_TOKENS.colors.rich_parchment,
              marginBottom: '24px',
              lineHeight: '1.6'
            }}
          >
            We've sent a password reset link to <strong>{email}</strong>
          </p>

          <p
            style={{
              fontFamily: RESET_TOKENS.typography.input,
              fontSize: '14px',
              color: `${RESET_TOKENS.colors.rich_parchment}80`,
              marginBottom: '32px',
              lineHeight: '1.5'
            }}
          >
            If you don't see the email, check your spam folder or try again with a different email address.
          </p>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setEmail('');
                setError('');
              }}
              style={{
                background: 'transparent',
                border: `1px solid ${RESET_TOKENS.colors.antique_gold}`,
                borderRadius: '8px',
                color: RESET_TOKENS.colors.antique_gold,
                padding: '12px 20px',
                fontSize: '14px',
                fontFamily: RESET_TOKENS.typography.button,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `${RESET_TOKENS.colors.antique_gold}15`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              Try Different Email
            </button>
            
            <button
              onClick={onNavigateHome}
              style={{
                background: `linear-gradient(135deg, ${RESET_TOKENS.colors.antique_gold} 0%, #E6C68A 100%)`,
                color: RESET_TOKENS.colors.slate,
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: RESET_TOKENS.typography.button,
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 16px ${RESET_TOKENS.colors.antique_gold}40`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${RESET_TOKENS.colors.slate} 0%, #1A202C 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
      }}
    >
      {/* Floating particles background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              background: RESET_TOKENS.colors.antique_gold,
              opacity: Math.random() * 0.3 + 0.1,
              animation: `float ${Math.random() * 10 + 10}s linear infinite`
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav style={{ 
        position: 'absolute', 
        top: window.innerWidth < 768 ? '16px' : '24px', 
        left: window.innerWidth < 768 ? '16px' : '24px',
        zIndex: 10
      }}>
        <button
          onClick={() => window.history.back()}
          style={{
            background: 'transparent',
            border: `1px solid ${RESET_TOKENS.colors.antique_gold}60`,
            borderRadius: '8px',
            color: 'white',
            padding: window.innerWidth < 768 ? '8px 16px' : '12px 20px',
            fontSize: window.innerWidth < 768 ? '12px' : '14px',
            fontFamily: RESET_TOKENS.typography.button,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = RESET_TOKENS.colors.antique_gold;
            e.target.style.background = `${RESET_TOKENS.colors.antique_gold}15`;
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = `${RESET_TOKENS.colors.antique_gold}60`;
            e.target.style.background = 'transparent';
          }}
        >
          <VoxSmythIcons.ArrowLeft size={14} color={RESET_TOKENS.colors.antique_gold} />
          Back
        </button>
      </nav>

      {/* Main Content */}
      <div className="relative z-10">
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '32px 24px' : '48px',
            width: '100%',
            maxWidth: window.innerWidth < 768 ? '100%' : RESET_TOKENS.layout.form_width,
            boxShadow: '0 20px 60px rgba(26, 32, 44, 0.3)',
            border: `1px solid ${RESET_TOKENS.colors.antique_gold}20`
          }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: window.innerWidth < 768 ? '24px' : '32px' }}>
            <div style={{ marginBottom: '16px' }}>
              <VoxSmythIcons.Lock size={window.innerWidth < 768 ? 40 : 48} color={RESET_TOKENS.colors.literary_gold} />
            </div>
            
            <h1
              style={{
                fontFamily: RESET_TOKENS.typography.main_title,
                fontSize: window.innerWidth < 768 ? '24px' : '32px',
                color: RESET_TOKENS.colors.slate,
                margin: '0 0 8px 0'
              }}
            >
              Forgot Your Password?
            </h1>
            
            <p
              style={{
                fontFamily: RESET_TOKENS.typography.input,
                fontSize: window.innerWidth < 768 ? '14px' : '16px',
                color: `${RESET_TOKENS.colors.slate}80`,
                margin: 0,
                maxWidth: '400px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              No worries! Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: RESET_TOKENS.colors.error_red + '15',
                border: `1px solid ${RESET_TOKENS.colors.error_red}30`,
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                color: RESET_TOKENS.colors.error_red,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <VoxSmythIcons.AlertTriangle size={16} color={RESET_TOKENS.colors.error_red} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email Input */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: RESET_TOKENS.typography.label,
                  fontSize: '14px',
                  fontWeight: '500',
                  color: RESET_TOKENS.colors.slate,
                  marginBottom: '6px'
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  height: RESET_TOKENS.layout.input_height,
                  padding: '0 16px',
                  border: `1px solid ${RESET_TOKENS.colors.slate}30`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: RESET_TOKENS.typography.input,
                  color: RESET_TOKENS.colors.slate,
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = RESET_TOKENS.colors.literary_gold;
                  e.target.style.boxShadow = `0 0 0 3px ${RESET_TOKENS.colors.literary_gold}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = `${RESET_TOKENS.colors.slate}30`;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <small style={{ 
                color: `${RESET_TOKENS.colors.slate}60`,
                fontSize: '12px'
              }}>
                We'll send a secure reset link to this email address
              </small>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !email}
              style={{
                width: '100%',
                height: '48px',
                background: (loading || !email) 
                  ? `${RESET_TOKENS.colors.literary_gold}50` 
                  : `linear-gradient(135deg, ${RESET_TOKENS.colors.literary_gold} 0%, ${RESET_TOKENS.colors.antique_gold} 100%)`,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: RESET_TOKENS.typography.button,
                fontWeight: '600',
                cursor: (loading || !email) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading && email) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 6px 20px ${RESET_TOKENS.colors.literary_gold}30`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && email) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {loading ? (
                <>
                  <VoxSmythIcons.Loading size={16} color="white" />
                  Sending Reset Link...
                </>
              ) : (
                <>
                  <VoxSmythIcons.Mail size={16} color="white" />
                  Send Reset Link
                </>
              )}
            </button>

            {/* Back to Sign In */}
            <div style={{ textAlign: 'center' }}>
              <p
                style={{
                  fontFamily: RESET_TOKENS.typography.input,
                  fontSize: '14px',
                  color: `${RESET_TOKENS.colors.slate}60`,
                  margin: 0
                }}
              >
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => window.location.href = '/auth'}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: RESET_TOKENS.colors.literary_gold,
                    fontSize: '14px',
                    fontFamily: RESET_TOKENS.typography.input,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = RESET_TOKENS.colors.antique_gold;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = RESET_TOKENS.colors.literary_gold;
                  }}
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;